<template>
  <section class="mt-5 mb-5">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8">

          <!-- Top bar -->
          <div class="page-bar py-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1">My Contacts</div>
                <div class="dropdown">
                  <button class="dot-btn btn" type="button" @click="openSearchModal" :disabled="searching">
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky custom tabs -->
          <div class="sticky-tabs mt-3">
            <div class="tabs" role="tablist" aria-label="Contacts tabs">
              <span class="tab-pill" :style="pillStyle" aria-hidden="true"></span>

              <button
                class="tab-btn"
                :class="{ active: activeTab === 'my' }"
                role="tab"
                :aria-selected="String(activeTab === 'my')"
                aria-controls="panel-my"
                id="tab-my"
                @click="switchTab('my')"
              >
                Requests
              </button>

              <button
                class="tab-btn"
                :class="{ active: activeTab === 'shared' }"
                role="tab"
                :aria-selected="String(activeTab === 'shared')"
                aria-controls="panel-shared"
                id="tab-shared"
                @click="switchTab('shared')"
              >
                Friends
              </button>
            </div>
          </div>

          <!-- Loader -->
          <div v-if="loading" class="loading-wrap">
            <div class="spinner"></div>
            <div class="muted small">Loading…</div>
          </div>

          <!-- Requests -->
          <div
            v-show="activeTab === 'my' && !loading"
            id="panel-my"
            role="tabpanel"
            aria-labelledby="tab-my"
          >
            <ul class="contact-card" role="list">
              <li
                class="contact-item"
                v-for="row in myrequests"
                :key="row.id ?? row.user_id ?? row.username"
              >
                <div class="d-flex align-items-center">
                  <!-- Avatar -->
                  <div class="flex-shrink-0 me-3">
                    <img
                      :src="avatarSrc(row.name || row.username, row.user_img)"
                      :alt="`${row.name || row.username || 'User'}'s profile picture`"
                      class="rounded-circle avatar"
                      loading="lazy"
                    />
                  </div>

                  <!-- Info -->
                  <div class="flex-grow-1 overflow-hidden pl-2">
                    <h5 class="h6 mb-1 text-truncate">
                      {{ capitalize(row.name) || '—' }}
                    </h5>
                    <span class="small text-muted text-truncate d-block">
                      @{{ row.username || 'unknown' }}
                    </span>
                  </div>

                  <!-- Actions -->
                  <div class="flex-shrink-0 ms-3">
                    <!-- Incoming pending: approve + cancel (reqstatus === 1) -->
                    <template v-if="isIncomingPending(row)">
                      <button
                        class="action-btn approve mr-2"
                        :disabled="busyId === getId(row)"
                        @click="approveIncoming(row)"
                      >
                        <span v-if="busyId !== getId(row)">
                          <i class="fas fa-check-circle"></i>
                          Approve
                        </span>
                        <span v-else class="spinner-border spinner-border-sm"></span>
                      </button>

                      <button
                        class="action-btn ghost danger"
                        :disabled="busyId === getId(row)"
                        @click="confirmCancel(row, 'incoming')"
                      >
                        <i class="fas fa-times"></i>
                        Cancel
                      </button>
                    </template>

                    <!-- Outgoing pending: pending + cancel (status === 0 or reqstatus === 0) -->
                    <template v-else-if="isOutgoingPending(row)"> 
                      <button
                        class="action-btn pending"
                        :disabled="busyId === getId(row)"
                        @click="confirmCancel(row, 'outgoing')"
                      >
                        <i class="fas fa-hourglass-half"></i>
                        Pending
                      </button>
                    </template>

                    <!-- Fallback -->
                    <template v-else>
                      <span class="badge badge-secondary">—</span>
                    </template>
                  </div>
                </div>
              </li>
            </ul>

            <div v-if="!myrequests.length" class="empty-state">
              <p class="muted mb-0">No requests found.</p>
            </div>
          </div>

          <!-- Friends -->
          <div
            v-show="activeTab === 'shared' && !loading"
            id="panel-shared"
            role="tabpanel"
            aria-labelledby="tab-shared"
          >
            <ul class="contact-card" role="list">
              <li
                class="contact-item"
                v-for="row in myfriends"
                :key="row.id ?? row.user_id ?? row.username"
              >
                <router-link
                  class="d-flex align-items-center friend-link"
                  :to="profileRoute(row)"
                  :aria-label="`View ${row.username || row.name || 'user'} profile`"
                >
                  <!-- Avatar -->
                  <div class="flex-shrink-0 me-3">
                    <img
                      :src="avatarSrc(row.name || row.username, row.user_img)"
                      :alt="`${row.name || row.username || 'User'}'s profile picture`"
                      class="rounded-circle avatar"
                      loading="lazy"
                    />
                  </div>

                  <!-- Info -->
                  <div class="flex-grow-1 overflow-hidden pl-2">
                    <h5 class="h6 mb-1 text-truncate">
                      {{ capitalize(row.name) || '—' }}
                    </h5>
                    <span class="small text-muted text-truncate d-block">
                      @{{ row.username || 'unknown' }}
                    </span>
                  </div>
                </router-link>
              </li>
            </ul>

            <div v-if="!myfriends.length" class="empty-state">
              <p class="muted mb-0">No friends found.</p>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Search Modal -->
    <div v-if="showSearch" class="modal-overlay" @click.self="closeSearchModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="searchTitle">
        <div class="modal-header">
          <h5 id="searchTitle" class="mb-0">Search Contacts</h5>
          <button class="close-btn" @click="closeSearchModal" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body">
          <div class="input-group mb-3">
            <input
              v-model.trim="searchKey"
              type="text"
              class="form-control"
              placeholder="Search by name or username…"
              @keyup.enter="doSearch"
              autofocus
            />
            <div class="input-group-append">
              <button class="btn btn-primary" :disabled="searching || !searchKey" @click="doSearch">
                <span v-if="!searching"><i class="fas fa-search mr-1"></i> Search</span>
                <span v-else class="spinner-border spinner-border-sm"></span>
              </button>
            </div>
          </div>

          <!-- Search results -->
          <div v-if="searching" class="muted small">Searching…</div>
          <ul v-else class="contact-card" role="list">
            <li
              class="contact-item"
              v-for="user in searchResults"
              :key="getId(user) || user.username"
            >
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0 me-3">
                  <img
                    :src="avatarSrc(user.name || user.username, user.user_img)"
                    :alt="`${user.name || user.username || 'User'}'s profile`"
                    class="rounded-circle avatar"
                  />
                </div>

                <div class="flex-grow-1 overflow-hidden pl-2">
                  <div class="h6 mb-1 text-truncate">{{ capitalize(user.name) || '—' }}</div>
                  <div class="small text-muted text-truncate">@{{ user.username || 'unknown' }}</div>
                </div>

                <div class="flex-shrink-0 ms-3">
                  <button
                    class="action-btn approve" v-if="user.reqstatus == 0"
                    :disabled="busyId === getId(user)"
                    @click="connectToUser(user)"
                  >
                    <span v-if="busyId !== getId(user)">Connect</span>
                    <span v-else class="spinner-border spinner-border-sm"></span>
                  </button>
                </div>
              </div>
            </li>
          </ul>

          <div v-if="!searching && searchResults.length === 0 && searchTried" class="empty-state">
            <p class="muted mb-0">No results found.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeSearchModal">Close</button>
        </div>
      </div>
    </div>

  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import { useHead } from '@vueuse/head'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import BackButton from '@/components/common/BackButton.vue'

const session = useSessionStore()

useHead({
  title: 'My Contacts - Invi8',
  meta: [
    { name: 'description', content: 'Manage your requests and friends on Invi8.' },
    { name: 'keywords', content: 'contacts, friends, requests, vue 3' },
  ],
})

const activeTab   = ref('my')
const myrequests  = ref([])
const myfriends   = ref([])
const loading     = ref(false)

const showSearch     = ref(false)
const searchKey      = ref('')
const searching      = ref(false)
const searchResults  = ref([])
const searchTried    = ref(false)

const busyId = ref(null) // per-row busy state (req/cancel/approve/connect)

/** Sliding pill styles (two tabs layout) */
const pillStyle = computed(() => ({
  transform: activeTab.value === 'my' ? 'translateX(0%)' : 'translateX(100%)'
}))

/* ----------------------------- Helpers ----------------------------- */
const getId        = (row) => Number(row?.user_id ?? row?.userid ?? row?.id ?? 0) || 0
const getFromId    = (row) => Number(row?.from_userid ?? row?.from_user_id ?? row?.fromuserid ?? 0) || 0
const getToId      = (row) => Number(row?.to_userid ?? row?.touserid ?? row?.toUserId ?? 0) || 0
const getReqStatus = (row) => Number(row?.reqstatus ?? row?.request_status ?? row?.status ?? 0) || 0
const getStatus    = (row) => Number(row?.status ?? 0) || 0

const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const initialOf = (nameOrUsername) => {
  const s = (nameOrUsername || '').toString().trim()
  if (!s) return 'U'
  return s[0].toUpperCase()
}

const avatarSrc = (name, img) => {
  if (img && String(img).trim() !== '') return img
  const initial = initialOf(name)
  return `https://dummyimage.com/200x200/222/fff.png?text=${encodeURIComponent(initial)}`
}

const profileRoute = (row) => {
  const handle = (row?.username || row?.user_name || row?.userid || row?.user_id || '').toString().trim()
  return handle ? `/profile/${encodeURIComponent(handle)}` : '/profile'
}

/* Status logic for requests:
   - Incoming pending: reqstatus === 1  -> show Approve + Cancel
   - Outgoing pending: status === 0 OR reqstatus === 0  -> show Pending + Cancel
*/
const isIncomingPending = (row) => getReqStatus(row) === 2
const isOutgoingPending = (row) => getStatus(row) === 1 || getReqStatus(row) === 1

/* ----------------------------- API ----------------------------- */
async function getMyContacts() {
  const formData = new FormData()
  formData.append('userid', session.userId)
  const res = await api.getMyContacts(formData).catch(() => null)

  const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
  if (ok) {
    const data = res?.data?.data ?? res?.data ?? []
    myrequests.value = Array.isArray(data) ? data : []
  } else {
    myrequests.value = []
  }
}

async function getMyFriends() {
  const formData = new FormData()
  formData.append('userid', session.userId)
  const res = await api.getMyFriends(formData).catch(() => null)

  const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
  if (ok) {
    const data = res?.data?.data ?? res?.data ?? []
    myfriends.value = Array.isArray(data) ? data : []
  } else {
    myfriends.value = []
  }
}

/* ----------------------- Search modal flows ----------------------- */
function openSearchModal() {
  showSearch.value = true
  searchKey.value = ''
  searchResults.value = []
  searchTried.value = false
}
function closeSearchModal() {
  showSearch.value = false
}

async function doSearch() {
  if (!searchKey.value) return
  searching.value = true
  searchTried.value = true
  searchResults.value = []

  const fd = new FormData()
  fd.append('userid', session.userId)
  fd.append('key', searchKey.value)

  const res = await api.searchUsersInfo(fd).catch(() => null)
  const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
  if (ok) {
    const data = res?.data?.data ?? res?.data ?? []
    searchResults.value = Array.isArray(data) ? data : []
  } else {
    searchResults.value = []
  }

  searching.value = false
}

async function connectToUser(user) {
  const touserid = getId(user)
  if (!touserid) return

  busyId.value = touserid
  const fd = new FormData()
  fd.append('userid', session.userId)
  fd.append('touserid', touserid)

  const res = await api.sendRequestContact(fd).catch(() => null)
  busyId.value = null

  const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
  if (ok) {
    // reflect as pending outgoing; you might also want to refresh requests
    await getMyContacts()
    await doSearch()
  }
}

/* ------------------- Approve / Cancel actions -------------------- */
async function approveIncoming(row) {
  const fromuserid = getFromId(row) || getId(row) // fallback
  if (!fromuserid) return

  busyId.value = getId(row) || fromuserid
  const fd = new FormData()
  fd.append('userid', session.userId)
  fd.append('fromuserid', fromuserid)

  const res = await api.approveRequest(fd).catch(() => null)
  busyId.value = null

  const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
  if (ok) {
    await getMyContacts()
    if (activeTab.value === 'shared') await getMyFriends()
  }
}

async function confirmCancel(row, kind) {
  const touserid = kind === 'incoming'
    ? (getFromId(row) || getId(row))
    : (getToId(row) || getId(row))

  if (!touserid) return

  const result = await Swal.fire({
    title: 'Cancel request?',
    text: 'Do you want to cancel this contact request?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, cancel',
    cancelButtonText: 'No',
    confirmButtonColor: '#d33'
  })

  if (!result.isConfirmed) return

  busyId.value = getId(row) || touserid

  const fd = new FormData()
  fd.append('userid', session.userId)
  fd.append('touserid', touserid)

  const res = await api.cancelRequest(fd).catch(() => null)
  busyId.value = null

  const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
  if (ok) {
    await getMyContacts()
  }
}

/* --------------------------- Tabs flow --------------------------- */
async function switchTab(tab) {
  if (activeTab.value === tab && (tab === 'my' ? myrequests.value.length : myfriends.value.length)) {
    return
  }
  activeTab.value = tab
  loading.value = true

  if (tab === 'my') {
    await getMyContacts()
  } else {
    await getMyFriends()
  }

  loading.value = false
}

// initial load
switchTab('my')
</script>

<style scoped>
/* Sticky container for tabs */
.sticky-tabs {
  position: sticky;
  top: 70px; /* adjust for your header */
  z-index: 999;
  background: #fff;
  padding-top: .25rem;
}

/* Tabs wrapper – segmented control */
.tabs {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #dbe3f0;
  border-radius: 12px;
  background: #f6f8fc;
  overflow: hidden;
}

/* Sliding pill (active tab background) */
.tab-pill {
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: #fff;
  box-shadow: 0 2px 10px rgba(13,110,253,.15);
  border-radius: 12px;
  transition: transform .25s ease;
  z-index: 0;
}

/* Tab buttons */
.tab-btn {
  position: relative;
  z-index: 1; /* above pill */
  appearance: none;
  background: transparent;
  border: 0;
  padding: .75rem 1rem;
  font-weight: 700;
  color: #5b6b82;
  cursor: pointer;
  transition: color .2s ease, background .2s ease;
  outline: none;
}
.tab-btn:hover {
  color: #2b3a55;
}
.tab-btn.active {
  color: #FB7210; /* active color */
}
.tab-btn:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(13,110,253,.35);
  border-radius: 10px;
}

/* Loader */
.loading-wrap {
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: 1rem 0;
}
.spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cdd9ed;
  border-top-color: #FB7210;
  animation: spin .9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.muted { color: #6c757d; }

/* Basic utility */
.avatar {
  width: 50px;
  height: 50px;
  object-fit: cover;
  background: #222;
  color: #fff;
  display: inline-block;
  border: 1px solid #00000010;
  box-shadow: 0 1px 3px rgba(0,0,0,.06);
}

.contact-card {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #edf1f7;
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(15,23,42,.08);
  background: #fff;
}
.contact-item {
  padding: 1rem 1.1rem;
  border-bottom: 1px solid #f3f5f9;
}
.contact-item:last-child {
  border-bottom: none;
}
.friend-link{
  text-decoration: none;
  color: inherit;
  width: 100%;
}
.friend-link:hover .h6{
  text-decoration: underline;
}

.empty-state {
  padding: 1rem;
  text-align: center;
}
.small { font-size: .9rem; }

.action-btn {
  border: none;
  border-radius: 999px;
  font-weight: 600;
  font-size: .85rem;
  padding: .45rem 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  line-height: 1.2;
  transition: transform .15s ease, box-shadow .2s ease, background .2s ease, color .2s ease;
}
.action-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}
.action-btn.approve {
  background: linear-gradient(120deg, #0baa5f, #0b8e4f);
  color: #fff;
  box-shadow: 0 6px 18px rgba(11, 138, 83, 0.3);
}
.action-btn.approve:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(11, 138, 83, 0.35);
}
.action-btn.ghost {
  border: 1px solid #d8e2f0;
  background: #fff;
  color: #4b5563;
}
.action-btn.ghost:hover:not(:disabled) {
  background: #f5f7fb;
}
.action-btn.ghost.danger {
  border-color: #fca5a5;
  color: #dc2626;
}
.action-btn.ghost.danger:hover:not(:disabled) {
  background: #fee2e2;
  color: #b91c1c;
}
.action-btn.pending {
  background: #f4f6fb;
  border: 1px dashed #cbd5f5;
  color: #5f6c85;
}
.action-btn.pending:hover:not(:disabled) {
  background: #e8ecf7;
}

.badge { display:inline-block; padding:.35rem .5rem; border-radius:6px; font-weight:600; }
.badge-warning { background:#fff3cd; color:#664d03; border:1px solid #ffe69c; }
.badge-secondary{ background:#e2e3e5; color:#41464b; border:1px solid #d3d6d8; }

/* Helpers */
.pl-2 { padding-left: .5rem; }
.me-3 { margin-right: 1rem; }
.ms-3 { margin-left: 1rem; }
.mr-2 { margin-right: .75rem; }

/* Search modal (simple, no Bootstrap JS) */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000;
}
.modal-card {
  width: min(720px, 92vw);
  max-height: 86vh;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.25);
  display: flex; flex-direction: column;
}
.modal-header, .modal-footer {
  padding: .85rem 1rem;
  border-bottom: 1px solid #f1f3f5;
}
.modal-footer { border-top: 1px solid #f1f3f5; border-bottom: 0; }
.modal-body { padding: 1rem; overflow: auto; }
.close-btn {
  background: transparent; border: 0; font-size: 1.25rem; line-height: 1;
  color: #6c757d; cursor: pointer;
}
.close-btn:hover { color: #000; }

/* Mobile tweaks */
@media (max-width: 576px) {
  .list-group-item { padding: 1rem !important; }
  .avatar { width: 40px; height: 40px; }
}
</style>
